// How much recording this device still has room for.
//
// A web capture is written to IndexedDB segment by segment while it runs, so a
// browser origin that is out of quota loses audio DURING an encounter — the loudest
// possible moment to discover it. The rescue paths (the server backstop, the
// assembler's gap fill) recover that audio after the fact; this is the cheap check
// that stops it happening at all, by warning the clinician BEFORE they start
// talking rather than four minutes in.
//
// Best-effort by construction: unsupported browsers, private modes and non-browser
// environments return null, which every caller must read as "unknown, say nothing"
// — never as "low".

// A segment is captured at 48 kbps (useRecorder's audioBitsPerSecond) = 6 KB/s, and
// stored as a base64 data URL, which inflates it by ~4/3. That is ~480 KB per
// minute; rounded up so the estimate errs toward warning early.
export const RECORDING_BYTES_PER_MINUTE = 500 * 1000;

// Warn below half an hour of headroom: long enough that a normal encounter finishes
// with room to spare, short enough that the warning stays rare and therefore
// credible. (Storage is shared with the rest of the app's caches, so the real
// margin is smaller than the arithmetic suggests.)
export const WARN_BELOW_MINUTES = 30;

// Minutes of recording the free space can hold. Pure, so the threshold is testable
// without a Storage Manager.
export function recordingMinutesAvailable(freeBytes) {
  if (typeof freeBytes !== "number" || !Number.isFinite(freeBytes) || freeBytes <= 0) return 0;
  return Math.floor(freeBytes / RECORDING_BYTES_PER_MINUTE);
}

export function isHeadroomLow(headroom) {
  // null/unknown is NOT low: never warn on a browser that simply cannot answer.
  if (!headroom || typeof headroom.minutes !== "number") return false;
  return headroom.minutes < WARN_BELOW_MINUTES;
}

// Are we running inside a frame? The Chrome extension is a side panel that iframes
// the app, and that is the one surface where the recording queue could silently
// become a DIFFERENT, evictable IndexedDB bucket — see the storage-partitioning note
// in chrome-app/sidepanel/sidepanel.html. Reported with the headroom so a partitioned
// context is visible in the field rather than inferred after audio goes missing.
// A cross-origin `window.top` access throws, which itself means embedded.
export function isEmbedded() {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch (_) {
    return true;
  }
}

// Whether this origin's storage is exempt from the browser's eviction pool
// (useIndexDb requests it at startup). null when the browser cannot answer.
async function persistedState() {
  try {
    if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.persisted) return null;
    return await navigator.storage.persisted();
  } catch (_) {
    return null;
  }
}

// { quota, usage, freeBytes, minutes, embedded, persisted } — or null when the
// browser cannot answer. Never throws: a storage-permission failure must not break
// the recorder's start.
export async function estimateRecordingHeadroom() {
  try {
    if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.estimate) return null;
    const { quota, usage } = (await navigator.storage.estimate()) || {};
    // Both fields are optional in the spec and absent in some engines/private modes.
    if (typeof quota !== "number" || typeof usage !== "number") return null;
    const freeBytes = Math.max(0, quota - usage);
    return {
      quota,
      usage,
      freeBytes,
      minutes: recordingMinutesAvailable(freeBytes),
      embedded: isEmbedded(),
      persisted: await persistedState(),
    };
  } catch (e) {
    return null;
  }
}
